import { Controller } from "@hotwired/stimulus"

// The add-expense sheet offers three ways in — Scan, Speak, Type — one panel
// each, laid side by side and swiped horizontally. The glass bar under the deck
// is the same choice as the swipe: tapping a tab scrolls the deck, and scrolling
// the deck lights the tab, so neither can disagree with the other.
//
// The transcript <textarea> deliberately lives BELOW the deck rather than
// inside a panel. Speak and Type are the same field with a different way of
// filling it, and voice_modal_controller owns exactly one transcript target —
// duplicating it per panel would leave the second copy unread.
//
// Below the deck sits one slot with two fillings — the transcript for Speak and
// Type, the photo sources for Scan — held to one height so switching tabs never
// resizes the sheet. Scan is presentation only right now: its two buttons are
// inert until capture is wired.
//
// Usage:
//   <div data-controller="expense-deck" data-expense-deck-index-value="0">
//     <div class="expense-deck" data-expense-deck-target="deck"
//          data-action="scroll->expense-deck#onScroll"> …panels… </div>
//     <div data-expense-deck-target="field">…</div>
//     <div data-expense-deck-target="photoRow">…</div>
//     <button data-expense-deck-target="tab" data-index="0"
//             data-action="click->expense-deck#select">…</button>
//   </div>
const SCAN_INDEX = 0

export default class extends Controller {
  static targets = ["deck", "tab", "dock", "thumb", "field", "photoRow"]
  static values = { index: { type: Number, default: 0 } }

  connect() {
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this._onResize = this._reposition.bind(this)
    window.addEventListener("resize", this._onResize)

    // Scan every time the page loads. Within a session the deck still reopens
    // on whichever way in was used last, because indexValue lives on the sheet
    // and the sheet outlives each open — but a reload starts at the camera.
    this._apply(this.indexValue, { animate: false })
    // Labels can still reflow as fonts land; the thumb is placed off the rects,
    // so it is measured once more after that has happened.
    requestAnimationFrame(() => this._moveThumb(this.indexValue))

    // The dock is a floating pane of the sheet now, so the sheet hides it
    // outright while loading or confirming. A hidden element has no rects, and
    // a thumb measured against no rects lands at zero — watching the box means
    // the measurement is retaken the moment there is a box to measure.
    if (this.hasDockTarget && typeof ResizeObserver !== "undefined") {
      this._dockObserver = new ResizeObserver(() => this._moveThumb(this.indexValue))
      this._dockObserver.observe(this.dockTarget)
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    this._dockObserver?.disconnect()
    clearTimeout(this._settleTimer)
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(index)) return
    this._apply(index)
  }

  // Same thing without a DOM event behind it. The native dock calls this
  // through bridge--expense-dock, which has an index and no button to click.
  selectIndex(index) {
    const parsed = Number(index)
    if (Number.isNaN(parsed)) return
    this._apply(parsed)
  }

  // Swipes drive the tabs. Rounding to the nearest panel means a half-finished
  // drag that snaps back does not leave the dock pointing at the wrong way in.
  //
  // Scroll events fired by our OWN smooth scroll are ignored: a jump from Scan
  // to Type passes over Speak, and reacting to that would re-target the pill
  // mid-flight — the pill would visibly stop at Speak and set off again.
  onScroll() {
    if (this._settling) return
    if (this._rafPending) return
    this._rafPending = true
    requestAnimationFrame(() => {
      this._rafPending = false
      const width = this.deckTarget.clientWidth
      if (!width) return
      const index = Math.round(Math.abs(this.deckTarget.scrollLeft) / width)
      if (index === this.indexValue) return
      this._apply(index, { scroll: false })
    })
  }

  // ── internals ─────────────────────────────────────────────────────────────

  _apply(index, { scroll = true, animate = true } = {}) {
    const clamped = Math.max(0, Math.min(index, this.tabTargets.length - 1))
    this.indexValue = clamped

    this.tabTargets.forEach((tab, i) => {
      const selected = i === clamped
      tab.setAttribute("aria-selected", String(selected))
      // .active is the glass nav's own selected hook — it is what keeps the
      // hover tint from fighting the selected tint.
      tab.classList.toggle("active", selected)
    })

    const onScan = clamped === SCAN_INDEX
    if (this.hasFieldTarget) this.fieldTarget.classList.toggle("hidden", onScan)
    if (this.hasPhotoRowTarget) this.photoRowTarget.classList.toggle("hidden", !onScan)

    this._moveThumb(clamped)

    // The mic belongs to Speak. Leaving that tab has to stop it, or it keeps
    // listening behind a panel that shows no sign of it.
    this.dispatch("tabchange", { detail: { index: clamped } })

    if (scroll) this._scrollTo(clamped, animate)
  }

  _scrollTo(index, animate) {
    const width = this.deckTarget.clientWidth
    if (!width) return
    // Right-to-left decks scroll into negative offsets, which is why the sign
    // is read off the computed direction rather than assumed.
    const rtl = getComputedStyle(this.deckTarget).direction === "rtl"
    const smooth = animate && !this._reducedMotion

    this._settling = true
    clearTimeout(this._settleTimer)
    this.deckTarget.scrollTo({
      left: (rtl ? -1 : 1) * index * width,
      behavior: smooth ? "smooth" : "auto"
    })
    this._settleTimer = setTimeout(() => { this._settling = false }, smooth ? 550 : 0)
  }

  // Every tab is the same width, so the thumb is a single translate — measured
  // off the live rects only so RTL and the bar's padding need no special case.
  _moveThumb(index) {
    if (!this.hasThumbTarget || !this.hasDockTarget) return
    const tab = this.tabTargets[index]
    if (!tab) return
    const dock = this.dockTarget.getBoundingClientRect()
    // Hidden: no box, so no measurement worth keeping. The ResizeObserver in
    // connect() calls back the moment the dock is laid out again.
    if (!dock.width) return
    // getBoundingClientRect measures border boxes, but `left: 0` on the thumb
    // resolves against the bar's padding box. The bar has a 1px border, which
    // is exactly the gap between the two.
    const border = parseFloat(getComputedStyle(this.dockTarget).borderLeftWidth) || 0
    const offset = tab.getBoundingClientRect().left - dock.left - border
    this.thumbTarget.style.transform = `translateX(${offset}px)`
  }

  _reposition() {
    this._moveThumb(this.indexValue)
    this._scrollTo(this.indexValue, false)
  }
}
